/*
  Tekeninvoer voor per-karakter-producten.

  De klant typt "11A". Wij zoeken de variant van elk teken op, tellen ze bij
  elkaar op en zetten ze in één keer in de winkelwagen via /cart/add.js. Elk
  karakter blijft dus zijn eigen regel met zijn eigen SKU — precies wat de
  leverancier nodig heeft — terwijl de klant maar één handeling doet.

  Overige opties (Kolor, Wysokość) komen uit Dawn's variant picker. Die staat
  buiten dit element, dus we lezen de gekozen waarden op documentniveau, net
  als de configurator doet.
*/
class OnvelaCharacters extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-role="variants"]');
    if (!raw) return;

    try {
      this.variants = JSON.parse(raw.textContent);
    } catch (e) {
      return;
    }

    this.charIndex = parseInt(this.dataset.charIndex, 10) - 1;
    this.max = parseInt(this.dataset.max, 10) || 8;

    this.input = this.querySelector('[data-role="input"]');
    this.counter = this.querySelector('[data-role="count"]');
    this.error = this.querySelector('[data-role="error"]');
    this.summary = this.querySelector('[data-role="summary"]');
    this.breakdown = this.querySelector('[data-role="breakdown"]');
    this.total = this.querySelector('[data-role="total"]');
    this.submit = this.querySelector('[data-role="submit"]');

    this.controller = new AbortController();
    const opts = { signal: this.controller.signal };

    this.input.addEventListener('input', () => this.render(), opts);
    this.submit.addEventListener('click', () => this.addToCart(), opts);
    // Andere opties zitten in Dawn's picker, buiten dit element.
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('input[type="radio"], select')) this.render();
    }, opts);

    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  /*
    Het teken dat bij een optiewaarde hoort.

    Meestal is de waarde het teken zelf ("7", "A"). Maar bij de 3D-cijfers heten
    twee waarden "Myślnik (-)" en "Ukośnik (/)" — een label met het teken tussen
    haakjes. Zonder deze vertaling kon je op dat product geen 12/3 of 12-A typen,
    terwijl we die karakters wel verkopen. Poolse adressen zitten er vol mee.
  */
  charOf(optionValue) {
    const m = String(optionValue).match(/\(([^)]+)\)\s*$/);
    return (m ? m[1] : optionValue).trim().toUpperCase();
  }

  /* De waarden van alle opties behalve "Znak", in de volgorde van het product. */
  otherOptions() {
    const picked = [
      ...document.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select option:checked'),
    ].map((el) => (el.value || el.textContent || '').trim());
    return picked;
  }

  /* Zoek de variant die hoort bij dit teken plus de overige gekozen opties. */
  findVariant(ch) {
    const others = this.otherOptions();
    return this.variants.find((v) => {
      if (this.charOf(v.opts[this.charIndex] || '') !== ch) return false;
      return v.opts.every((value, i) => {
        if (i === this.charIndex) return true;
        // Als de optie niet in de picker staat (één waarde), telt hij altijd mee.
        return others.length === 0 || others.includes(value);
      });
    });
  }

  parse() {
    // Alleen hoofdletters: de leverancier maakt geen kleine letters, dus we
    // corrigeren stil in plaats van de klant te laten ontdekken dat het niet kan.
    const value = (this.input.value || '').toUpperCase();
    if (this.input.value !== value) {
      const pos = this.input.selectionStart;
      this.input.value = value;
      this.input.setSelectionRange(pos, pos);
    }

    const chars = value.split('').filter((c) => c.trim() !== '');
    const found = [];
    const missing = [];

    chars.forEach((ch) => {
      const v = this.findVariant(ch);
      if (v && v.available) found.push({ ch, v });
      else if (!missing.includes(ch)) missing.push(ch);
    });

    return { chars, found, missing };
  }

  render() {
    const { chars, found, missing } = this.parse();

    this.counter.textContent = `${chars.length}/${this.max}`;

    if (missing.length > 0) {
      this.error.hidden = false;
      this.error.textContent = window.onvelaCharStrings.unavailable.replace(
        '[chars]',
        missing.join(', ')
      );
    } else {
      this.error.hidden = true;
    }

    const ok = found.length > 0 && missing.length === 0;
    this.submit.disabled = !ok;

    if (!ok) {
      this.summary.hidden = true;
      return;
    }

    const cents = found.reduce((sum, f) => sum + f.v.price, 0);
    this.summary.hidden = false;
    this.breakdown.textContent = window.onvelaCharStrings.breakdown
      .replace('[count]', found.length)
      .replace('[each]', this.money(found[0].v.price));
    this.total.textContent = this.money(cents);
  }

  money(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return (cents / 100).toFixed(2).replace('.', ',') + ' PLN';
  }

  async addToCart() {
    const { found } = this.parse();
    if (found.length === 0) return;

    // Gelijke tekens samenvoegen tot één regel met aantal: "111" wordt 3× de
    // variant "1", niet drie losse regels die de klant apart moet beheren.
    const byVariant = new Map();
    found.forEach(({ v }) => byVariant.set(v.id, (byVariant.get(v.id) || 0) + 1));

    const text = this.input.value.toUpperCase();
    const items = [...byVariant.entries()].map(([id, quantity]) => ({
      id,
      quantity,
      properties: { [window.onvelaCharStrings.property]: text },
    }));

    this.submit.disabled = true;
    this.submit.classList.add('is-loading');

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('cart');
      window.location = `${window.Shopify.routes.root}cart`;
    } catch (e) {
      this.error.hidden = false;
      this.error.textContent = window.onvelaCharStrings.error;
      this.submit.disabled = false;
    } finally {
      this.submit.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('onvela-characters')) {
  customElements.define('onvela-characters', OnvelaCharacters);
}
